import React, { useState, useCallback, useMemo, ChangeEvent, useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { useSelectedWorkpackUrlSync } from '../../../deeplink';
import { ExpandMore as DownArrow, KeyboardArrowRight as RightArrow } from '@mui/icons-material';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { Columns, TemplateTypeWithAllTab } from '@/types';
import { PointOfInterestTable } from '../PointOfInterestTable';
import { abyssColors } from '@/theme/colors';
import { TablePaginationContainer } from '../styles';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Accordion,
  IconButton,
  AccordionDetails,
} from '@mui/material';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { ALL_POIS_TAB_LABEL, NOT_APPLICABLE_LABEL, WORKPACK_TYPE_LABELS } from '@/constants';
import { StatusChangeDropDown } from './StatusChangeDropDown';
import { FieldVerificationStatus } from './FieldVerificationStatus';
import { StyledAccordionSummary, StyledTableRow } from './styles';
import { MenuOptions } from './MenuOptions';
import { accordionStyles } from '@/components/UserManagement/styles';
import {
  useGetPointOfInterestGroupedByWorkpackQuery,
  useScCategoryMappingQuery,
  WorkpackStatus,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import { allWorkpacks } from '@/components/Analysis/state';

type WorkpacksProps = {
  template: TemplateTypeWithAllTab;
  selectedPointOfInterests: string[];
  handlePointOfInterestSelect: (selection: string[]) => void;
  clearSelectedPOIs: () => void;
  selectedTab: string;
  columns: Columns[];
  isVerifiedFilters: boolean[];
  isGoverningFilters: boolean[];
  accessibilityFilters: string[];
  statusFilters: string[];
  selectedPoiFilters: {
    id: string;
    value: string[];
  }[];
  setSelectedFilterValues: React.Dispatch<React.SetStateAction<SelectedValues>>;
  selectedFilterValues: SelectedValues;
  setSelectedPointOfInterests?: React.Dispatch<React.SetStateAction<string[]>>;
  searchTerm?: string;
  handleResetFilters?: () => void;
  setWorkpackIds: React.Dispatch<React.SetStateAction<string[]>>;
  workpackIds: string[];
  deckFilters: string[];
};

const workpackAccordionColumns = [
  { label: 'Workpack', width: '15%' },
  { label: 'POIs', width: '8%' },
  { label: 'Workorders', width: '10%' },
  { label: 'Type', width: '10%' },
  { label: 'WBS', width: '10%' },
  { label: 'Components', width: '11%' },
  { label: 'Field Verification', width: '13%' },
  { label: 'Status', width: '14%' },
];
const { primary: colors, warning: warningColor } = abyssColors;

export const Workpacks = ({
  template,
  selectedPointOfInterests,
  handlePointOfInterestSelect,
  clearSelectedPOIs,
  selectedTab,
  columns,
  isVerifiedFilters,
  isGoverningFilters,
  accessibilityFilters,
  statusFilters,
  selectedPoiFilters,
  setSelectedFilterValues,
  selectedFilterValues,
  searchTerm,
  handleResetFilters,
  setWorkpackIds,
  workpackIds,
  deckFilters,
}: WorkpacksProps) => {
  const structureId = useGetStructureId();
  const [selectedWorkpack, setSelectedWorkpack] = useState<{ id?: string; rowId?: string }>({});
  useSelectedWorkpackUrlSync(selectedWorkpack, setSelectedWorkpack);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 30 });
  const [workpackStatusUpdateKey, setWorkpackStatusUpdateKey] = useState(0);

  const { data: scCategoryData, loading: scCategoryLoading } = useScCategoryMappingQuery({
    variables: { structureId },
    skip: !structureId,
  });

  const type =
    selectedTab === ALL_POIS_TAB_LABEL ? undefined : selectedTab.replace(/ /g, '_').toUpperCase();

  const {
    data: workpacksData,
    loading,
    refetch,
  } = useGetPointOfInterestGroupedByWorkpackQuery({
    variables: {
      input: {
        structureId,
        type,
        searchTerm,
      },
      pageInfo: {
        skip: pagination.page * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortBy: 'createdAt',
        sortDir: 1,
      },
    },
  });

  const setAllWorkpacks = useSetAtom(allWorkpacks);

  const { workpacks, totalCount } = useMemo(() => {
    const { allWorkpacks, workpacksCount } = workpacksData ?? {};

    const sortedByUnassigned = allWorkpacks
      ? allWorkpacks.slice().sort((a, b) => {
          if (a._id === null) return 1;
          if (b._id === null) return -1;
          return 0;
        })
      : [];

    return {
      workpacks: sortedByUnassigned,
      totalCount: workpacksCount?.count ?? 100,
    };
  }, [workpacksData]);

  // Set all workpacks in global state for KPI tracker
  useEffect(() => {
    if (workpacks && workpacks.length > 0) {
      const workpackList = workpacks.filter((wp) => wp._id && wp.workpack?.name);
      setAllWorkpacks(workpackList);
    }
  }, [workpacks, setAllWorkpacks]);

  const handleWorkpackStatusChange = useCallback(() => {
    // Refetch workpack data to update POI statuses in the table
    refetch();
    // Force re-render of POI table by updating key
    setWorkpackStatusUpdateKey((prev) => prev + 1);
  }, [refetch]);

  const handleExpandClick = useCallback(
    (id: string) => {
      setSelectedWorkpack((previous) => {
        const previousRowId = previous.rowId;
        const newId = id === previousRowId ? undefined : id;
        const isUnassignedWorkpack = id !== previousRowId && id === '';
        return { id: isUnassignedWorkpack ? 'unassigned' : newId, rowId: newId };
      });
      handleResetFilters?.();
    },
    [handleResetFilters]
  );

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = +event.target.value;
    setPagination({ page: 0, rowsPerPage: newRowsPerPage });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination((previous) => ({ ...previous, page: newPage }));
  };

  const getColor = useCallback(
    (id: string) => {
      if (!id) return warningColor[50];
      return selectedWorkpack.id === id ? colors[100] : colors[50];
    },
    [selectedWorkpack.id]
  );

  const scCategoryMappingExists = useMemo(() => {
    return Object.keys(scCategoryData?.structure?.scCategoryMapping ?? {}).length > 0;
  }, [scCategoryData?.structure?.scCategoryMapping]);

  if (loading) {
    return (
      <Box sx={{ mt: 25 }}>
        <PageLoader />
      </Box>
    );
  }

  if (!workpacks || workpacks?.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 10 }}>
        <FeedbackCircle text="No data found" />
      </Box>
    );
  }

  const columnWidths = [
    '48px', // expand icon
    ...workpackAccordionColumns.map((col) => col.width),
    '48px', // menu icon
  ];

  const renderColGroup = () => (
    <colgroup>
      {columnWidths.map((width, index) => (
        <col key={index} style={{ width }} />
      ))}
    </colgroup>
  );

  return (
    <TableContainer>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        {renderColGroup()}
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: '8px 16px' }}></TableCell>
            {workpackAccordionColumns.map((column) => (
              <TableCell key={column.label} sx={{ padding: '8px' }}>
                {column.label}
              </TableCell>
            ))}
            <TableCell sx={{ padding: '8px' }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workpacks.map((data) => {
            const workpackId = data?._id ?? '';
            const isOpen = selectedWorkpack.rowId === workpackId;
            const border = isOpen ? `1px solid ${colors[300]}` : 'none';
            const workpackIdForPoiTable =
              selectedWorkpack.id === 'unassigned' ? undefined : (selectedWorkpack.id ?? '');
            return (
              <StyledTableRow key={workpackId}>
                <TableCell colSpan={10} style={{ padding: 0, borderBottom: 'none' }}>
                  <Accordion
                    disableGutters
                    elevation={0}
                    expanded={isOpen}
                    sx={{ ...accordionStyles.root, mb: 1 }}
                  >
                    <StyledAccordionSummary
                      sx={{
                        border,
                        backgroundColor: getColor(workpackId),
                        '& .MuiAccordionSummary-content': { margin: 0, width: '100%' },
                        padding: 0,
                      }}
                    >
                      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                        {renderColGroup()}
                        <TableBody>
                          <TableRow sx={{ '& td': { borderBottom: 'none', padding: '8px' } }}>
                            <TableCell sx={{ padding: '8px 16px' }}>
                              <IconButton onClick={() => handleExpandClick(workpackId)}>
                                {isOpen ? <DownArrow /> : <RightArrow />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {workpackId ? data.workpack?.name : 'Un-assigned'}
                            </TableCell>
                            <TableCell>{data.pointOfInterestsCount}</TableCell>
                            <TableCell>N/A</TableCell>
                            <TableCell>
                              {data.workpack?.type
                                ? WORKPACK_TYPE_LABELS[data.workpack.type]
                                : NOT_APPLICABLE_LABEL}
                            </TableCell>
                            <TableCell>{data.workpack?.wbs ?? NOT_APPLICABLE_LABEL}</TableCell>
                            <TableCell>{data.assembliesCount}</TableCell>
                            <TableCell>
                              <FieldVerificationStatus
                                pointOfInterestsCount={data.pointOfInterestsCount}
                                verifiedPointOfInterestsCount={data.verifiedPointOfInterestsCount}
                              />
                            </TableCell>
                            <TableCell>
                              {workpackId && !scCategoryLoading && (
                                <StatusChangeDropDown
                                  workpackId={workpackId}
                                  status={data.workpack?.status as WorkpackStatus}
                                  showAlternateStatuses={scCategoryMappingExists}
                                  onStatusChange={handleWorkpackStatusChange}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {workpackId && data.workpack?.name && (
                                <MenuOptions
                                  workpackId={workpackId}
                                  workpackName={data.workpack.name}
                                  workpackWbs={data.workpack?.wbs}
                                  workpackType={data.workpack?.type}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </StyledAccordionSummary>
                    <AccordionDetails>
                      {isOpen && (
                        <PointOfInterestTable
                          key={`${workpackIdForPoiTable}-${workpackStatusUpdateKey}`}
                          template={template}
                          selectedPointOfInterests={selectedPointOfInterests}
                          handlePointOfInterestSelect={handlePointOfInterestSelect}
                          clearSelectedPOIs={clearSelectedPOIs}
                          selectedTab={selectedTab}
                          setSelectedFilterValues={setSelectedFilterValues}
                          selectedFilterValues={selectedFilterValues}
                          isVerifiedFilters={isVerifiedFilters}
                          accessibilityFilters={accessibilityFilters}
                          statusFilters={statusFilters}
                          selectedPoiFilters={selectedPoiFilters}
                          columns={columns}
                          workpackId={workpackIdForPoiTable}
                          excludePoisWithWorkpack={workpackIdForPoiTable === undefined}
                          viewBy={'workpack'}
                          setWorkpackIds={setWorkpackIds}
                          workpackIds={workpackIds}
                          isGoverningFilters={isGoverningFilters}
                          deckFilters={deckFilters}
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePaginationContainer>
        <TablePagination
          count={totalCount}
          page={pagination.page}
          rowsPerPageOptions={[
            { label: '30', value: 30 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
            { label: 'All', value: totalCount },
          ]}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onPageChange={handleChangePage}
        />
      </TablePaginationContainer>
    </TableContainer>
  );
};
